<template>
  <div>
    <!-- Modal Overlay -->
    <div 
      class="history-modal-overlay" 
      :class="{ 'is-visible': isOpen }"
      @click="$emit('close')"
    ></div>

    <!-- Modal Container -->
    <div 
      class="history-modal" 
      :class="{ 'is-open': isOpen }"
    >
      <!-- Drag Handle -->
      <div class="drag-handle" @click="$emit('close')">
        <div class="handle-bar"></div>
      </div>
      
      <!-- Header with Search -->
      <div class="modal-header">
        <div class="header-top">
          <h3 class="header-title">{{ $t('historyModal.title') }}</h3>
          <button class="close-icon-btn" @click="$emit('close')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
        
        <!-- Search Bar -->
        <div class="search-container">
          <div class="search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          </div>
          <input 
            type="text" 
            v-model="searchQuery"
            :placeholder="$t('historyModal.searchPlaceholder')"
            class="search-input"
          >
        </div>
        <div class="category-toolbar">
          <select v-model="categoryFilter" class="category-filter" aria-label="按分类筛选">
            <option value="all">全部分类</option>
            <option value="uncategorized">未分类</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
          </select>
          <button type="button" class="category-tool-btn" @click="createCategory">＋ 新建分类</button>
          <template v-if="selectedCategory">
            <button type="button" class="category-tool-btn" @click="renameCategory">重命名分类</button>
            <button type="button" class="category-tool-btn danger" @click="deleteCategory">删除分类</button>
          </template>
        </div>
      </div>

      <!-- Content List -->
      <div class="modal-content hide-scrollbar">
        <div v-if="filteredGroups.length === 0" class="empty-state">
          <p>{{ $t('historyModal.emptyState') }}</p>
        </div>
        
        <div v-for="group in filteredGroups" :key="group.label" class="session-group">
          <!-- Group Label -->
          <div class="group-label">
            <span>{{ group.label }}</span>
          </div>
          
          <!-- Chat Items -->
          <div
            v-for="session in group.sessions" 
            :key="session.id"
            class="session-card"
            :class="{ 'is-active': String(currentSessionId) === String(session.id) }"
            role="button"
            tabindex="0"
            @click="$emit('load-session', session.id)"
            @keydown.enter="$emit('load-session', session.id)"
          >
            <div v-if="String(currentSessionId) === String(session.id)" class="active-indicator"></div>
            <div class="session-card-inner">
              <div class="session-icon-wrapper" :class="{ 'is-active': String(currentSessionId) === String(session.id) }">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
              </div>
              <div class="session-info">
                <span class="session-title" :class="{ 'is-active': String(currentSessionId) === String(session.id) }">{{ session.title || '未命名会话' }}</span>
                <span class="session-meta">{{ $t('historyModal.sessionMeta', { time: formatTime(session.timestamp), count: session.messages ? session.messages.length : 0 }) }}</span>
                <span class="session-category-label">{{ categoryName(session) }}</span>
              </div>
            </div>
            <div class="session-actions" @click.stop>
              <button type="button" class="session-action-btn" title="重命名" aria-label="重命名对话" @click="renameSession(session)">✎</button>
              <select class="session-move-select" :value="session.categoryId || 'uncategorized'" aria-label="移动到分类" @change="moveSession(session, $event.target.value)">
                <option value="uncategorized">未分类</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
              </select>
              <button type="button" class="session-action-btn danger" title="删除" aria-label="删除对话" @click="$emit('delete-session', session.id)">×</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Action -->
      <div class="modal-footer">
        <button class="new-chat-btn" @click="$emit('new-chat')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          <span>{{ $t('historyModal.newChat') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mutations } from '@/store';
import { showToast } from '@/utils/toast';
import { showPrompt } from '@/utils/dialog';

export default {
  name: 'HistoryModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    sessions: {
      type: Array,
      default: () => []
    },
    currentSessionId: {
      type: [String, Number],
      default: ''
    },
    categories: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: '',
      categoryFilter: 'all'
    }
  },
  computed: {
    selectedCategory() {
      return this.categories.find(category => category.id === this.categoryFilter) || null;
    },
    filteredGroups() {
      if (!this.sessions) return []

      const query = this.searchQuery.toLowerCase();
      const filtered = this.sessions.filter(session => {
        const title = String(session.title || '').toLowerCase();
        const matchesQuery = title.includes(query);
        const matchesCategory = this.categoryFilter === 'all' ||
          (this.categoryFilter === 'uncategorized'
            ? !session.categoryId || !this.categories.some(category => category.id === session.categoryId)
            : session.categoryId === this.categoryFilter);
        return matchesQuery && matchesCategory;
      });
      
      // Grouping logic
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
      const yesterday = today - 86400000
      const lastWeek = today - 86400000 * 7
      
      const groups = [
        { label: this.$t('historyModal.groups.today'), sessions: [] },
        { label: this.$t('historyModal.groups.yesterday'), sessions: [] },
        { label: this.$t('historyModal.groups.lastWeek'), sessions: [] },
        { label: this.$t('historyModal.groups.earlier'), sessions: [] }
      ]
      
      filtered.forEach(session => {
        const time = session.timestamp || Date.now()
        if (time >= today) {
          groups[0].sessions.push(session)
        } else if (time >= yesterday) {
          groups[1].sessions.push(session)
        } else if (time >= lastWeek) {
          groups[2].sessions.push(session)
        } else {
          groups[3].sessions.push(session)
        }
      })
      
      return groups.filter(g => g.sessions.length > 0)
    }
  },
  methods: {
    categoryName(session) {
      const category = this.categories.find(item => item.id === session.categoryId);
      return category ? category.name : '未分类';
    },
    async renameSession(session) {
      const title = await showPrompt('重命名对话', session.title || '');
      if (!title || !title.trim()) return;
      mutations.renameSession(session.id, title).catch(error => showToast(error.message || '重命名失败', 'warning'));
    },
    moveSession(session, categoryId) {
      mutations.moveSessionToCategory(session.id, categoryId === 'uncategorized' ? null : categoryId)
        .catch(error => showToast(error.message || '移动会话失败', 'warning'));
    },
    async createCategory() {
      const name = await showPrompt('新建对话分类');
      if (!name || !name.trim()) return;
      try {
        const category = mutations.createSessionCategory(name);
        if (category) this.categoryFilter = category.id;
      } catch (error) { showToast(error.message || '创建分类失败', 'warning'); }
    },
    async renameCategory() {
      if (!this.selectedCategory) return;
      const name = await showPrompt('重命名分类', this.selectedCategory.name);
      if (!name || !name.trim()) return;
      try { mutations.renameSessionCategory(this.selectedCategory.id, name); }
      catch (error) { showToast(error.message || '重命名分类失败', 'warning'); }
    },
    async deleteCategory() {
      if (!this.selectedCategory) return;
      const category = this.selectedCategory;
      const count = this.sessions.filter(session => session.categoryId === category.id).length;
      const message = count
        ? `“${category.name}”中有 ${count} 个会话。强制删除将同时删除这些会话及其关联附件和工作区文件，且无法恢复。确定继续？`
        : `确定删除空分类“${category.name}”？`;
      if (!await window.confirm(message)) return;
      await mutations.deleteSessionCategory(category.id, true);
      this.categoryFilter = 'all';
    },
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const locale = this.$i18n && this.$i18n.locale === 'en' ? 'en-US' : 'zh-CN'
      
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
      }
      
      return new Intl.DateTimeFormat(locale, {
        month: this.$i18n && this.$i18n.locale === 'en' ? 'short' : 'numeric',
        day: 'numeric'
      }).format(date)
    }
  }
}
</script>

<style scoped>
.history-modal-overlay {
  position: absolute;
  inset: 0;
  z-index: 100;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  opacity: 0;
  pointer-events: none;
  transition: opacity 300ms;
}

.history-modal-overlay.is-visible {
  opacity: 1;
  pointer-events: auto;
}

.history-modal {
  --history-primary: #1A3BFF;
  --history-primary-hover: #1d4ed8;
  --history-primary-soft: rgba(26, 59, 255, 0.08);
  --history-primary-ring: rgba(26, 59, 255, 0.1);
  --history-bg: #ffffff;
  --history-content-bg: #F8F9FF;
  --history-card-bg: #ffffff;
  --history-input-bg: #f9fafb;
  --history-input-focus-bg: #ffffff;
  --history-muted-bg: #f3f4f6;
  --history-muted-hover-bg: #e5e7eb;
  --history-text: #111827;
  --history-text-secondary: #6b7280;
  --history-text-muted: #9ca3af;
  --history-icon-muted: #d1d5db;
  --history-border: #f3f4f6;
  --history-soft-border: #f9fafb;
  --history-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15);
  --history-card-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --history-card-hover-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  --history-card-active-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 85vh;
  background-color: var(--history-bg);
  border-top-left-radius: 32px;
  border-top-right-radius: 32px;
  box-shadow: var(--history-shadow);
  z-index: 105;
  display: flex;
  flex-direction: column;
  transform: translateY(100%);
  opacity: 0;
  transition: transform 500ms cubic-bezier(0.32, 0.72, 0, 1), opacity 300ms;
}

.history-modal.is-open {
  transform: translateY(0);
  opacity: 1;
}

@media (min-width: 768px) {
  .history-modal {
    bottom: auto;
    left: 50%;
    top: 50%;
    width: 600px;
    height: 70vh;
    max-height: 800px;
    border-radius: 48px;
    transform: translate(-50%, -45%) scale(0.95);
    opacity: 0;
    pointer-events: none;
    transition: transform 400ms cubic-bezier(0.32, 0.72, 0, 1), opacity 300ms, scale 300ms;
    overflow: hidden;
  }

  .history-modal.is-open {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
    pointer-events: auto;
  }
}

.drag-handle {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 12px 0 4px 0;
  cursor: pointer;
}

.handle-bar {
  width: 48px;
  height: 6px;
  background-color: var(--history-muted-bg);
  border-radius: 9999px;
}

.modal-header {
  display: flex;
  flex-direction: column;
  padding: 8px 20px 16px 20px;
  border-bottom: 1px solid var(--history-soft-border);
  gap: 16px;
}

.header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-title {
  font-size: 20px;
  font-weight: 800;
  color: var(--history-text);
  letter-spacing: -0.025em;
  margin: 0;
}

.close-icon-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--history-text-muted);
  background-color: var(--history-muted-bg);
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: all 150ms;
}

.close-icon-btn:hover {
  color: var(--history-text);
  background-color: var(--history-muted-hover-bg);
}

.close-icon-btn:active {
  transform: scale(0.95);
}

.search-container {
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--history-input-bg);
  border-radius: 9999px;
  padding: 4px;
  border: 1px solid var(--history-border);
  transition: all 200ms;
}

.search-container:focus-within {
  background-color: var(--history-input-focus-bg);
  border-color: rgba(26, 59, 255, 0.3);
  box-shadow: 0 0 0 4px var(--history-primary-ring);
}

.search-icon {
  padding-left: 12px;
  padding-right: 8px;
  color: var(--history-text-muted);
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  background: transparent;
  border: none;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--history-text);
  outline: none;
}

.search-input::placeholder {
  color: var(--history-text-muted);
}

.category-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.category-filter,
.category-tool-btn {
  min-height: 32px;
  border: 1px solid var(--history-border);
  border-radius: 10px;
  background: var(--history-card-bg);
  color: var(--history-text-secondary);
  padding: 0 9px;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.category-filter { min-width: 108px; }
.category-tool-btn:hover { border-color: rgba(26, 59, 255, .32); color: var(--history-primary); }
.category-tool-btn.danger { color: #dc2626; }

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  background-color: var(--history-content-bg);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.empty-state {
  padding: 40px;
  text-align: center;
  color: var(--history-text-secondary);
  font-size: 14px;
}

.session-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.group-label {
  padding: 8px 8px 4px 8px;
}

.group-label span {
  font-size: 12px;
  font-weight: 700;
  color: var(--history-text-muted);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.session-card {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 20px;
  background-color: var(--history-card-bg);
  border: 1px solid var(--history-border);
  box-shadow: var(--history-card-shadow);
  transition: all 200ms;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  text-align: left;
}

.session-card:focus-visible,
.session-action-btn:focus-visible,
.session-move-select:focus-visible {
  outline: 2px solid var(--history-primary);
  outline-offset: 2px;
}

.session-card:hover {
  border-color: rgba(26, 59, 255, 0.3);
  box-shadow: var(--history-card-hover-shadow);
}

.session-card.is-active {
  border-color: rgba(26, 59, 255, 0.2);
  box-shadow: var(--history-card-active-shadow);
}

.active-indicator {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: var(--history-primary);
}

.session-card-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  overflow: hidden;
  flex: 1;
}

.session-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background-color: var(--history-input-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--history-text-muted);
  flex-shrink: 0;
  transition: all 200ms;
}

.session-card:hover .session-icon-wrapper {
  background-color: var(--history-primary-soft);
  color: var(--history-primary);
}

.session-icon-wrapper.is-active {
  background-color: var(--history-primary-soft);
  color: var(--history-primary);
}

.session-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.session-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--history-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 200ms;
}

.session-card:hover .session-title,
.session-title.is-active {
  color: var(--history-primary);
}

.session-meta {
  font-size: 12px;
  color: var(--history-text-secondary);
  font-weight: 500;
  margin-top: 2px;
}

.session-category-label {
  align-self: flex-start;
  margin-top: 4px;
  color: var(--history-text-muted);
  font-size: 11px;
}

.session-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.session-action-btn,
.session-move-select {
  min-height: 30px;
  padding: 0 7px;
  border: 1px solid var(--history-border);
  border-radius: 9px;
  background: var(--history-card-bg);
  color: var(--history-text-secondary);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.session-action-btn:hover,
.session-move-select:hover { border-color: rgba(26, 59, 255, .3); color: var(--history-primary); }
.session-action-btn.danger { color: #dc2626; }

.modal-footer {
  padding: 20px;
  background-color: var(--history-bg);
  border-top: 1px solid var(--history-soft-border);
}

.new-chat-btn {
  width: 100%;
  background-color: var(--history-primary);
  color: #ffffff;
  border-radius: 20px;
  padding: 16px 0;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 8px 20px rgba(26, 59, 255, 0.25);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 150ms;
}

.new-chat-btn:hover {
  background-color: var(--history-primary-hover);
}

.new-chat-btn:active {
  transform: scale(0.98);
}
</style>

<style>
[data-theme="dark"] .history-modal-overlay {
  background-color: rgba(0, 0, 0, 0.55);
}

[data-theme="dark"] .history-modal {
  --history-primary: #4f71ff;
  --history-primary-hover: #6b8aff;
  --history-primary-soft: rgba(79, 113, 255, 0.14);
  --history-primary-ring: rgba(79, 113, 255, 0.16);
  --history-bg: #161925;
  --history-content-bg: #0f111a;
  --history-card-bg: #1a1d27;
  --history-input-bg: rgba(255, 255, 255, 0.06);
  --history-input-focus-bg: rgba(255, 255, 255, 0.08);
  --history-muted-bg: rgba(255, 255, 255, 0.08);
  --history-muted-hover-bg: rgba(255, 255, 255, 0.12);
  --history-text: #f1f5f9;
  --history-text-secondary: #94a3b8;
  --history-text-muted: #64748b;
  --history-icon-muted: #475569;
  --history-border: rgba(255, 255, 255, 0.1);
  --history-soft-border: rgba(255, 255, 255, 0.08);
  --history-shadow: 0 -10px 40px rgba(0, 0, 0, 0.4);
  --history-card-shadow: none;
  --history-card-hover-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
  --history-card-active-shadow: 0 0 0 1px rgba(79, 113, 255, 0.12);
}
</style>
